import React from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  IconButton,
  ButtonGroup,
  Card,
} from "@chakra-ui/react";
import { timeAgo } from "@keita/lanepatrol-sdk";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../App";
import {
  Notification,
  NotificationAction,
  NotificationEntity,
  NotificationType,
} from "../models/Notification";
import { tLifeCycleStage } from "../../cra/models/LifeCycleStage";
import { DeleteIcon, ViewIcon, ViewOffIcon } from "../../shared/views/icons";
import { useStores } from "../../shared/stores/RootStore";
import Avatar from "../../shared/views/CustomAvatar";

interface Props {
  onAction?: () => void;
  notification: Notification;
}
export const NotificationItem: React.FC<Props> = ({
  notification,
  onAction,
}) => {
  const navigate = useNavigate();
  const { notificationStore } = useStores();
  const entityBased = notification.type === NotificationType.Entity;
  const opacity = notification.read ? 0.6 : 1;

  const onRead = () => {
    notificationStore.readNotificationById(notification.id);
    onAction?.();
  };

  const onUnread = () => {
    notificationStore.unreadNotificationById(notification.id);
    onAction?.();
  };

  const onArchive = () => {
    notificationStore.archiveNotificationById(notification.id);
    onAction?.();
  };

  return (
    <HStack p={2} fontSize="sm" w="100%">
      <Box alignSelf="start" opacity={opacity} minW="24px" mr={2}>
        {entityBased && <Avatar size="xs" name={notification.createdByEmail} />}
      </Box>
      <VStack flexGrow={1} minW="0" alignItems="stretch">
        <HStack alignItems="start">
          <Box
            opacity={opacity}
            fontSize="sm"
            flexGrow={1}
            flexShrink={1}
            overflow="hidden"
            wordWrap="normal"
          >
            <Text as="span" mr={2}>
              {getI18nLabel(notification)}
            </Text>
            <Text as="span" fontSize="xs" opacity={0.6}>
              {timeAgo(new Date(notification.createdAt))}
            </Text>
          </Box>
          <ButtonGroup size="sm" variant="ghost" colorPalette="primary">
            <IconButton
              title={t(
                `notification:markAs${notification.read ? "Unread" : "Read"}`
              )}
             
              onClick={notification.read ? onUnread : onRead}
            >
              {notification.read ? <ViewOffIcon /> : <ViewIcon />}
            </IconButton>
            <IconButton
              title={t("notification:archive")}
              aria-label={t("notification:archive")}
              onClick={onArchive}
            >
              <DeleteIcon />
            </IconButton>
          </ButtonGroup>
        </HStack>

        {entityBased && (
          <Box opacity={opacity}>
            <Card.Root
              variant="outline"
              width="100%"
              py={2}
              px={3}
              cursor="pointer"
              onClick={() => {
                navigate(entityUrl(notification));
              }}
            >
              {renderEntity(notification)}
            </Card.Root>
          </Box>
        )}
      </VStack>
    </HStack>
  );
};

const entityUrl = (notification: Notification) => {
  switch (notification.entity) {
    case NotificationEntity.CraWay:
      return appRoutes.craWay(notification.entityId);
    case NotificationEntity.Survey:
      return appRoutes.survey(notification.entityId);
    case NotificationEntity.Comment:
      return appRoutes.comment(notification.entityId);
    default:
      return "/";
  }
};

const renderEntity = (notification: Notification) => {
  switch (notification.entity) {
    case NotificationEntity.CraWay:
      return (
        <>
          <Text fontWeight="semibold">{notification.args.name}</Text>
          <Text fontSize="xs">{`${notification.args.city}: ${notification.args.surveyName}`}</Text>
        </>
      );
    case NotificationEntity.Survey:
      return (
        <>
          <Text fontWeight="semibold">{notification.args.name}</Text>
          <Text fontSize="xs">{`${notification.args.country}: ${notification.args.city}`}</Text>
        </>
      );
    case NotificationEntity.Comment:
      return (
        <>
          <Text fontWeight="semibold">{`${notification.args.surveyCity}: ${notification.args.wayName}`}</Text>
          <Text fontSize="xs" lineClamp={2}>
            {notification.args.plainText}
          </Text>
        </>
      );
    default:
      return null;
  }
};

const getI18nLabel = (n: Notification) => {
  var i18nKey = `notification:subject.${n.action}`;
  const values: Record<string, string | number> = {
    email: n.createdByEmail!,
  };

  switch (n.action) {
    case NotificationAction.WebAppUploadProcessed: {
      i18nKey = `notification:subject.${n.action}.${
        n.args.success === "true" ? "success" : "error"
      }`;
      values.count = parseInt(n.args.nWarnings);
      break;
    }
    case NotificationAction.LifeCycleStageUpdated: {
      values.lifeCycleStage = tLifeCycleStage(n.args.lifeCycleStage);
      break;
    }
  }

  return <Trans i18nKey={i18nKey} values={values} />;
};
